import { useAuthUser } from "react-auth-kit";
import { buildDeleteOptions, buildGetOptions, buildPostOptions, fetchWithErrorHandling } from "services/FetchUtils";
import { getRegistryURL } from "services/registry/Registry";

export enum PackageState {
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED",
}

export interface IPackageWad {
  id: string;
  description?: string;
}

export function wadToString(wad: IPackageWad): string {
  return `${wad.id}${wad.description ? ` | ${wad.description}` : ""}`;
}

export interface IPackage {
  id: string;
  wads: IPackageWad[];
  createdTime: number;
  state: PackageState;
  publishedTime?: number;
  description?: string;
  bucketPath?: string;
}
const endpoint = (action: string): string => {
  return `${getRegistryURL()}/v1/registry/package/${action}`;
};

export const listPackages = async (accessToken: string) => {
  const options = buildPostOptions(accessToken, {});
  return fetchWithErrorHandling(endpoint("list"), options);
};

export const create = async (accessToken: string) => {
  const options = buildPostOptions(accessToken, {});
  return fetchWithErrorHandling(endpoint("create"), options);
};

export const remove = async (accessToken: string, id: string) => {
  const options = buildDeleteOptions(accessToken);
  return fetchWithErrorHandling(endpoint(`remove/${id}`), options);
};

export const publish = async (accessToken: string, id: string) => {
  const options = buildPostOptions(accessToken, { id });
  return fetchWithErrorHandling(endpoint("publish"), options);
};

export const update = async (accessToken: string, id: string, wads: string[], description: string) => {
  const options = buildPostOptions(accessToken, {
    id,
    wads,
    description,
  });
  return fetchWithErrorHandling(endpoint("update"), options);
};

export const duplicate = async (accessToken: string, id: string) => {
  const options = buildPostOptions(accessToken, { id });
  return fetchWithErrorHandling(endpoint("duplicate"), options);
};

export const getPackageContent = async (accessToken: string, id: string) => {
  const options = buildGetOptions(accessToken);
  return fetchWithErrorHandling(endpoint(`get-content/${id}`), options);
};

export const usePackagesService = () => {
  const authUser = useAuthUser();
  const accessToken = authUser()?.access_token;
  return {
    listPackages: () => listPackages(accessToken),
    create: () => create(accessToken),
    remove: (id: string) => remove(accessToken, id),
    publish: (id: string) => publish(accessToken, id),
    update: (id: string, wads: string[], description: string) => update(accessToken, id, wads, description),
    duplicate: (id: string) => duplicate(accessToken, id),
    getPackageContent: (id: string) => getPackageContent(accessToken, id),
  };
};
